import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Tv2, Film, ArrowLeft, Loader2, Youtube, Plus, Sparkles,
  FileText, BadgeCheck, CheckCircle2, Clapperboard, Settings2, RefreshCw,
  Users, Eye, PlayCircle, TrendingUp, BarChart3, ExternalLink, ThumbsUp,
  MessageSquare, Wand2, CalendarClock, AlertCircle, Zap, Flame, ChevronDown, Compass,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import api, { imageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ACCENT = "#00e5ff";

const fmt = (n) => {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, "") + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, "") + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, "") + "K";
  return String(n);
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

const relTime = (iso) => {
  if (!iso) return "—";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "today";
  if (days === 1) return "1d ago";
  if (days < 30) return `${days}d ago`;
  const mo = Math.floor(days / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(days / 365)}y ago`;
};

const AMBER = "#f5b301";
const TEAL = "#22d3ee";

function Sparkline({ data, color, testid }) {
  const raw = (data || []).filter((v) => v != null);
  const pts = raw.length >= 2 ? raw : raw.length === 1 ? [raw[0], raw[0]] : [0, 0];
  const w = 260, h = 54, pad = 3;
  const min = Math.min(...pts), max = Math.max(...pts);
  const range = max - min || 1;
  const n = pts.length;
  const x = (i) => (i / (n - 1)) * (w - pad * 2) + pad;
  const y = (v) => h - pad - ((v - min) / range) * (h - pad * 2);
  const line = pts.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${line} L${x(n - 1).toFixed(1)},${h} L${x(0).toFixed(1)},${h} Z`;
  const gid = `spark-${testid}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-[54px] w-full" data-testid={testid}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function StatTile({ label, value, testid }) {
  return (
    <div className="rounded-md border border-[#272a35] bg-[#0e1015] px-4 py-3" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#6b7280]">{label}</p>
      <p className="mt-1 font-display text-2xl font-bold leading-none text-[#f3f4f6]">{value}</p>
    </div>
  );
}

function ChannelAvatar({ channel }) {
  const ref = (channel.references || [])[0];
  if (ref?.image_id) {
    return (
      <img
        src={imageUrl(ref.image_id)}
        alt={channel.name}
        className="h-14 w-14 shrink-0 rounded-full border border-[#272a35] object-cover"
      />
    );
  }
  const initial = (channel.name || "?").trim().charAt(0).toUpperCase();
  return (
    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#00e5ff]/40 bg-[#0e1015] font-display text-xl font-bold text-[#00e5ff]">
      {initial}
    </div>
  );
}

function YtStat({ icon: Icon, label, value, testid }) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-[#272a35] bg-[#0e1015] px-3 py-2" data-testid={testid}>
      <Icon className="h-4 w-4 shrink-0 text-[#ff0033]" />
      <div className="min-w-0">
        <p className="truncate font-display text-sm font-bold leading-none text-[#f3f4f6]">{value}</p>
        <p className="mt-0.5 text-[9px] uppercase tracking-[0.15em] text-[#6b7280]">{label}</p>
      </div>
    </div>
  );
}

function tierColor(tier) {
  const t = (tier || "").toLowerCase();
  if (t === "viral") return "#22c55e";
  if (t === "strong") return "#00e5ff";
  if (t === "underperforming") return "#ef4444";
  return "#f5b301"; // Average / default
}

function VideoRow({ channelId, video, avgViews, subscribers }) {
  const [an, setAn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const analyze = async () => {
    if (an) { setOpen((o) => !o); return; }
    setLoading(true);
    try {
      const res = await api.post(`/channels/${channelId}/youtube/video-analysis`, {
        video_id: video.video_id, title: video.title, views: video.views,
        likes: video.likes, comments: video.comments, published_at: video.published_at,
        channel_avg_views: avgViews || 0, subscribers: subscribers || 0,
      });
      setAn(res.data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not analyze this video");
    } finally {
      setLoading(false);
    }
  };

  const color = an ? tierColor(an.tier) : "#6b7280";
  const m = an?.metrics || {};

  return (
    <div className="rounded-md border border-[#272a35] bg-[#14161c] transition-colors" data-testid={`yt-video-${video.video_id}`}>
      <div className="flex items-center gap-3 p-2">
        {video.thumbnail && <img src={video.thumbnail} alt="" className="h-12 w-20 shrink-0 rounded object-cover" />}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-[#f3f4f6]">{video.title}</p>
          <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-[#9ca3af]">
            <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{fmt(video.views)}</span>
            <span className="flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{fmt(video.likes)}</span>
            <span className="flex items-center gap-1"><MessageSquare className="h-3 w-3" />{fmt(video.comments)}</span>
            <span>{fmtDate(video.published_at)}</span>
            {an && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold" style={{ color, background: `${color}1a`, border: `1px solid ${color}55` }}>
                <Flame className="h-3 w-3" /> {an.viral_score}/100 · {an.tier}
              </span>
            )}
          </div>
        </div>
        <button
          data-testid={`yt-analyze-video-${video.video_id}`}
          onClick={analyze}
          disabled={loading}
          className="flex shrink-0 items-center gap-1 rounded-md border border-[#a855f7]/50 bg-[#a855f7]/10 px-2.5 py-1.5 text-[11px] font-semibold text-[#c084fc] transition-colors hover:bg-[#a855f7]/20 disabled:opacity-50"
          title="Analyze this video's viral potential"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : an ? <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> : <Zap className="h-3.5 w-3.5" />}
          {loading ? "Analyzing…" : an ? (open ? "Hide" : "Show") : "Analyze"}
        </button>
        <a href={video.url} target="_blank" rel="noreferrer" className="shrink-0 text-[#6b7280] hover:text-[#00e5ff]" title="Open on YouTube">
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {open && an && (
        <div className="space-y-3 border-t border-[#272a35] p-3" data-testid={`yt-analysis-${video.video_id}`}>
          <div className="flex items-start gap-3">
            <div className="flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-lg" style={{ background: `${color}1a`, border: `1px solid ${color}55` }}>
              <span className="font-display text-lg font-bold leading-none" style={{ color }}>{an.viral_score}</span>
              <span className="text-[8px] uppercase tracking-wide text-[#9ca3af]">score</span>
            </div>
            <p className="flex-1 text-sm text-[#e5e7eb]">{an.verdict}</p>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <MiniMetric label="Views/day" value={fmt(m.views_per_day)} />
            <MiniMetric label="Engagement" value={m.engagement_rate != null ? `${m.engagement_rate}%` : "—"} />
            <MiniMetric label="vs channel avg" value={m.vs_channel_avg != null ? `${m.vs_channel_avg}x` : "—"} />
            <MiniMetric label="Views / subs" value={m.views_vs_subs_pct != null ? `${m.views_vs_subs_pct}%` : "—"} />
          </div>

          {(an.what_worked || []).length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#22c55e]">What worked</p>
              <ul className="list-disc space-y-1 pl-5 text-xs text-[#cbd5e1]">{an.what_worked.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          {(an.what_didnt || []).length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#ef4444]">What held it back</p>
              <ul className="list-disc space-y-1 pl-5 text-xs text-[#cbd5e1]">{an.what_didnt.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
          {(an.suggestions || []).length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#00e5ff]">Do next</p>
              <ul className="list-disc space-y-1 pl-5 text-xs text-[#cbd5e1]">{an.suggestions.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MiniMetric({ label, value }) {
  return (
    <div className="rounded-md border border-[#272a35] bg-[#0e1015] px-2 py-1.5 text-center">
      <p className="font-mono text-sm font-bold text-[#f3f4f6]">{value}</p>
      <p className="text-[8px] uppercase tracking-[0.12em] text-[#6b7280]">{label}</p>
    </div>
  );
}

function ChannelYouTubeModal({ open, onClose, channel, yt, autoAi = false }) {
  const [ai, setAi] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const runAi = useCallback(async () => {
    setAiLoading(true);
    try {
      const res = await api.post(`/channels/${channel.channel_id}/youtube/ai-summary`);
      setAi(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate AI insights");
    } finally {
      setAiLoading(false);
    }
  }, [channel.channel_id]);

  useEffect(() => {
    if (open && autoAi && !ai && !aiLoading) runAi();
  }, [open, autoAi]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = yt?.status || {};
  const ins = yt?.insights || {};
  const videos = yt?.videos || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid={`yt-modal-${channel.channel_id}`}
        className="max-h-[90vh] w-[95vw] max-w-3xl overflow-y-auto overflow-x-hidden [&>*]:min-w-0 border-[#272a35] bg-[#0e1015] text-[#f3f4f6]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <Youtube className="h-5 w-5 text-[#ff0033]" />
            {s.title || channel.name} — Live YouTube Analytics
          </DialogTitle>
          <DialogDescription className="text-[#9ca3af]">
            {s.url ? (
              <a href={s.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#00e5ff] hover:underline">
                @{s.handle} <ExternalLink className="h-3 w-3" />
              </a>
            ) : `@${(channel.handle || "").replace(/^@/, "")}`}
          </DialogDescription>
        </DialogHeader>

        {/* Big stats */}
        <div className="grid grid-cols-3 gap-3">
          <YtStat icon={Users} label="Subscribers" value={s.subscribers_hidden ? "Hidden" : fmt(s.subscribers)} testid={`yt-modal-subs-${channel.channel_id}`} />
          <YtStat icon={Eye} label="Total views" value={fmt(s.total_views)} testid={`yt-modal-views-${channel.channel_id}`} />
          <YtStat icon={PlayCircle} label="Uploads" value={fmt(s.video_count)} testid={`yt-modal-uploads-${channel.channel_id}`} />
        </div>

        {/* Insights */}
        <div className="mt-2 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <YtStat icon={BarChart3} label="Avg views (recent)" value={fmt(ins.avg_views)} />
          <YtStat icon={TrendingUp} label="Median views" value={fmt(ins.median_views)} />
          <YtStat icon={CalendarClock} label="Upload cadence" value={ins.upload_cadence_days != null ? `${ins.upload_cadence_days}d` : "—"} />
          <YtStat icon={CalendarClock} label="Last upload" value={fmtDate(ins.last_upload)} />
        </div>

        {/* AI insights */}
        <div className="mt-3 rounded-lg border border-[#a855f7]/30 bg-[#a855f7]/[0.05] p-4">
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2 font-display text-sm font-bold text-[#c084fc]">
              <Wand2 className="h-4 w-4" /> AI growth insights
            </p>
            {!ai && (
              <Button
                data-testid={`yt-ai-btn-${channel.channel_id}`}
                onClick={runAi}
                disabled={aiLoading}
                className="shrink-0 rounded-md bg-[#a855f7] text-white hover:bg-[#9333ea]"
                size="sm"
              >
                {aiLoading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                {aiLoading ? "Analyzing…" : "Generate"}
              </Button>
            )}
          </div>
          {ai && (
            <div className="mt-3 space-y-3 text-sm" data-testid={`yt-ai-result-${channel.channel_id}`}>
              <p className="text-[#e5e7eb]">{ai.summary}</p>
              {(ai.working || []).length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#22c55e]">What's working</p>
                  <ul className="list-disc space-y-1 pl-5 text-[#cbd5e1]">
                    {ai.working.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
              {(ai.suggestions || []).length > 0 && (
                <div>
                  <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#00e5ff]">Suggestions</p>
                  <ul className="list-disc space-y-1 pl-5 text-[#cbd5e1]">
                    {ai.suggestions.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Recent videos */}
        <div className="mt-3">
          <p className="mb-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-[#6b7280]">
            <Zap className="h-3 w-3 text-[#a855f7]" /> Recent videos — analyze viral potential
          </p>
          <div className="space-y-2">
            {videos.length === 0 && <p className="text-sm text-[#6b7280]">No recent uploads found.</p>}
            {videos.map((v) => (
              <VideoRow
                key={v.video_id}
                channelId={channel.channel_id}
                video={v}
                avgViews={ins.avg_views}
                subscribers={s.subscribers}
              />
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function TermStat({ label, value, delta, testid }) {
  const showDelta = delta != null;
  return (
    <div className="border border-[#1f2430] bg-[#0b0d12] px-3 py-2.5" data-testid={testid}>
      <p className="text-[9px] uppercase tracking-[0.22em] text-[#6b7280]">{label}</p>
      <p className="mt-1 font-mono text-xl font-bold leading-none text-[#f3f4f6]">{value}</p>
      {showDelta && (
        <p className="mt-1 font-mono text-[10px]" style={{ color: delta > 0 ? AMBER : "#6b7280" }}>
          {delta >= 0 ? "+" : ""}{fmt(delta)} · 7d
        </p>
      )}
    </div>
  );
}

function ChartBox({ label, data, color, testid }) {
  const collecting = (data || []).length < 2;
  return (
    <div className="border border-[#1f2430] bg-[#0b0d12] p-2.5">
      <p className="mb-1 font-mono text-[9px] uppercase tracking-[0.18em]" style={{ color }}>{label}</p>
      <Sparkline data={data} color={color} testid={testid} />
      {collecting && <p className="mt-0.5 font-mono text-[8px] text-[#4b5563]">collecting daily data…</p>}
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-0.5">
      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#6b7280]">{label}</span>
      <span className="font-mono text-[11px] text-[#e5e7eb]">{value}</span>
    </div>
  );
}

function TermBtn({ icon: Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className="flex items-center gap-1.5 border border-[#22d3ee]/40 bg-[#22d3ee]/[0.04] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.12em] text-[#22d3ee] transition-colors hover:bg-[#22d3ee]/10"
    >
      <Icon className="h-3 w-3" /> {label}
    </button>
  );
}

function ChannelCard({ channel, onNewStory, onManage, onOpenStory, onHide }) {
  const [yt, setYt] = useState(null);
  const [ytLoading, setYtLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [autoAi, setAutoAi] = useState(false);
  const [syncedAt, setSyncedAt] = useState(null);

  const fetchYt = useCallback(() => {
    if (!channel.handle) { setYt({ found: false, reason: "no_handle" }); return; }
    setYtLoading(true);
    api.get(`/channels/${channel.channel_id}/youtube`)
      .then((res) => { setYt(res.data); setSyncedAt(new Date()); })
      .catch(() => setYt({ found: false, reason: "error" }))
      .finally(() => setYtLoading(false));
  }, [channel.channel_id, channel.handle]);

  useEffect(() => { fetchYt(); }, [fetchYt]);

  const openModal = (ai) => { setAutoAi(ai); setModalOpen(true); };

  const st = yt?.status;
  const ins = yt?.insights || {};
  const deltas = yt?.deltas || {};
  const history = yt?.history || [];
  const subsHist = history.map((h) => h.subscribers);
  const viewsHist = history.map((h) => h.views);

  const initial = (channel.name || "?").trim().charAt(0).toUpperCase();
  const ref = (channel.references || [])[0];

  return (
    <div
      data-testid={`dashboard-channel-${channel.channel_id}`}
      className="flex flex-col border border-[#1f2430] bg-[#08090c] p-5 transition-colors hover:border-[#22d3ee]/30"
    >
      {/* Header */}
      <div className="flex items-start gap-3">
        {st?.thumbnail ? (
          <img src={st.thumbnail} alt="" className="h-12 w-12 shrink-0 rounded-full border border-[#1f2430] object-cover" />
        ) : ref?.image_id ? (
          <img src={imageUrl(ref.image_id)} alt="" className="h-12 w-12 shrink-0 rounded-full border border-[#1f2430] object-cover" />
        ) : (
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-[#22d3ee]/40 bg-[#0b0d12] font-mono text-lg font-bold text-[#22d3ee]">{initial}</div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-lg font-bold leading-tight tracking-tight text-[#f3f4f6]">
            {st?.title || channel.name || "Untitled channel"}
          </h3>
          <p className="truncate font-mono text-xs text-[#6b7280]">@{(st?.handle || channel.handle || "").replace(/^@/, "") || "no-handle"}</p>
          {st?.category && (
            <span className="mt-1.5 inline-flex items-center gap-1 border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em]" style={{ borderColor: `${AMBER}55`, color: AMBER, background: `${AMBER}0d` }}>
              ◆ {st.category}
            </span>
          )}
        </div>
      </div>

      { ytLoading && !st ? (
        <div className="mt-5 flex items-center gap-2 font-mono text-xs text-[#6b7280]">
          <Loader2 className="h-4 w-4 animate-spin" /> syncing with YouTube…
        </div>
      ) : !st ? (
        <div className="mt-5 flex items-center gap-2 border border-dashed border-[#1f2430] bg-[#0b0d12] px-3 py-3 font-mono text-[11px] text-[#9ca3af]" data-testid={`yt-empty-${channel.channel_id}`}>
          <AlertCircle className="h-4 w-4 shrink-0 text-[#f5b301]" />
          {yt?.reason === "no_handle"
            ? "Add a YouTube @handle (Manage) to sync stats."
            : yt?.reason === "channel_not_found"
              ? `No YouTube channel found for @${(channel.handle || "").replace(/^@/, "")}.`
              : "Couldn't load YouTube data."}
        </div>
      ) : (
        <>
          {/* Top stats */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            <TermStat label="Subs" value={st.subscribers_hidden ? "Hidden" : fmt(st.subscribers)} delta={deltas.subs_7d} testid={`yt-subs-${channel.channel_id}`} />
            <TermStat label="Views" value={fmt(st.total_views)} testid={`yt-views-${channel.channel_id}`} />
            <TermStat label="Videos" value={fmt(st.video_count)} testid={`yt-uploads-${channel.channel_id}`} />
          </div>

          {/* Charts */}
          <div className="mt-2 grid grid-cols-2 gap-2">
            <ChartBox label="Subs · Last 30 days" data={subsHist} color={AMBER} testid={`yt-chart-subs-${channel.channel_id}`} />
            <ChartBox label="Views · Last 30 days" data={viewsHist} color={TEAL} testid={`yt-chart-views-${channel.channel_id}`} />
          </div>

          {/* Details */}
          <div className="mt-3 grid grid-cols-1 gap-x-6 gap-y-0 sm:grid-cols-2">
            <DetailRow label="Avg views / video" value={fmt(st.avg_views_per_video)} />
            <DetailRow label="Posts / month" value={st.posts_per_month ?? "—"} />
            <DetailRow label="Last upload" value={relTime(ins.last_upload)} />
            <DetailRow label="Created" value={fmtDate(st.published_at)} />
            <DetailRow label="Est. monthly rev" value={st.est_monthly_rev != null ? `$${fmt(st.est_monthly_rev)}` : "—"} />
            <DetailRow label="RPM" value={`$${st.rpm ?? 4}`} />
          </div>

          {/* Actions */}
          <div className="mt-4 flex flex-wrap gap-2">
            <TermBtn icon={TrendingUp} label="Analyze" onClick={() => openModal(true)} testid={`dashboard-analyze-${channel.channel_id}`} />
            <TermBtn icon={PlayCircle} label="Videos" onClick={() => openModal(false)} testid={`dashboard-videos-${channel.channel_id}`} />
            <TermBtn icon={Plus} label="Next video" onClick={() => onNewStory(channel.channel_id)} testid={`dashboard-nextvideo-${channel.channel_id}`} />
          </div>
        </>
      )}

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between border-t border-[#1f2430] pt-3 font-mono text-[10px] uppercase tracking-[0.12em] text-[#4b5563]">
        <span>{syncedAt ? `Synced ${fmtDate(syncedAt.toISOString())}` : "Not synced"}</span>
        <div className="flex items-center gap-3">
          <button data-testid={`dashboard-refresh-${channel.channel_id}`} onClick={fetchYt} className="flex items-center gap-1 hover:text-[#22d3ee]">
            <RefreshCw className={`h-3 w-3 ${ytLoading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button data-testid={`dashboard-manage-${channel.channel_id}`} onClick={() => onManage(channel.channel_id)} className="flex items-center gap-1 hover:text-[#22d3ee]">
            <Settings2 className="h-3 w-3" /> Manage
          </button>
          <button data-testid={`dashboard-hide-${channel.channel_id}`} onClick={() => onHide(channel.channel_id)} className="flex items-center gap-1 hover:text-red-400">
            ✕ Remove
          </button>
        </div>
      </div>

      {st && (
        <ChannelYouTubeModal open={modalOpen} onClose={() => setModalOpen(false)} channel={channel} yt={yt} autoAi={autoAi} />
      )}
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/dashboard");
      setData(res.data);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onNewStory = (channelId) => navigate("/studio", { state: { newFromChannelId: channelId } });
  const onManage = (channelId) => navigate("/channels", { state: { editChannelId: channelId } });
  const onOpenStory = (projectId) => navigate("/studio", { state: { openProjectId: projectId } });

  const totals = data?.totals || { channels: 0, stories: 0, published: 0, videos: 0 };
  const [hidden, setHidden] = useState([]);
  const onHide = (channelId) => setHidden((h) => [...h, channelId]);
  const channels = (data?.channels || []).filter((c) => !hidden.includes(c.channel_id));

  const NavBtn = ({ icon: Icon, label, testid, onClick, active }) => (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
        active ? "bg-[#1d2028] text-[#f3f4f6] border border-[#00e5ff]/40" : "text-[#9ca3af] hover:bg-[#14161c] hover:text-[#f3f4f6] border border-transparent"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <div className="grain flex h-screen overflow-hidden bg-[#0b0c10] text-[#f3f4f6]">
      {/* Left rail */}
      <aside className="flex h-full w-72 shrink-0 flex-col border-r border-[#272a35] bg-[#0e1015]">
        <div className="flex items-center gap-2 px-5 h-16 border-b border-[#272a35]">
          <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-md bg-white">
            <img src={`${process.env.PUBLIC_URL}/ytf-logo.png`} alt="YouTube Factory" className="h-full w-full object-contain" />
          </div>
          <span className="font-display font-bold tracking-tight">YouTube Factory <span className="text-[#00e5ff]">AI</span></span>
        </div>

        <div className="p-3 space-y-1">
          <NavBtn icon={LayoutDashboard} label="Dashboard" testid="nav-dashboard" active onClick={() => {}} />
          <NavBtn icon={Compass} label="Niches" testid="nav-niches" onClick={() => navigate("/niches")} />
          <NavBtn icon={Tv2} label="Channels" testid="nav-channels" onClick={() => navigate("/channels")} />
          <NavBtn icon={Film} label="Studio" testid="nav-studio" onClick={() => navigate("/studio")} />
        </div>

        <div className="mt-2 flex-1 overflow-y-auto px-4">
          <p className="overline">Overview</p>
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2 text-sm">
              <span className="text-[#9ca3af]">Channels</span><span className="font-semibold">{totals.channels}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2 text-sm">
              <span className="text-[#9ca3af]">Total stories</span><span className="font-semibold">{totals.stories}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2 text-sm">
              <span className="text-[#9ca3af]">Published</span><span className="font-semibold text-[#eab308]">{totals.published}</span>
            </div>
            <div className="flex items-center justify-between rounded-md border border-[#272a35] bg-[#14161c] px-3 py-2 text-sm">
              <span className="text-[#9ca3af]">Assembled videos</span><span className="font-semibold text-[#22c55e]">{totals.videos}</span>
            </div>
          </div>
        </div>

        <div className="border-t border-[#272a35] p-4">
          <div className="flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1d2028] text-[#00e5ff]"><Youtube className="h-4 w-4" /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="truncate text-sm font-medium">{user?.name || "Signed in"}</p>
              <p className="truncate text-xs text-[#9ca3af]">{user?.email || ""}</p>
            </div>
            <button onClick={logout} title="Sign out" className="text-[#9ca3af] hover:text-red-400 transition-colors">
              <ArrowLeft className="h-4 w-4 rotate-180" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="z-20 flex h-16 shrink-0 items-center justify-between border-b border-[#272a35] bg-black/60 px-6 backdrop-blur-xl">
          <div>
            <p className="overline !text-white">Dashboard</p>
            <h1 className="font-display text-base font-semibold tracking-tight leading-none mt-0.5">All channels</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              data-testid="dashboard-refresh-btn"
              onClick={() => { setLoading(true); load(); }}
              variant="outline"
              className="rounded-md border-[#272a35] bg-transparent text-[#f3f4f6] hover:bg-[#1d2028] hover:text-[#f3f4f6]"
            >
              <RefreshCw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button
              data-testid="dashboard-new-channel-btn"
              onClick={() => navigate("/channels", { state: { editChannelId: "__new__" } })}
              className="rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
            >
              <Plus className="mr-2 h-4 w-4" /> New channel
            </Button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#9ca3af]" /></div>
          ) : channels.length === 0 ? (
            <div className="mx-auto max-w-md rounded-lg border border-dashed border-[#272a35] bg-[#14161c] p-10 text-center" data-testid="dashboard-empty">
              <Tv2 className="mx-auto h-10 w-10 text-[#00e5ff]" />
              <h2 className="mt-4 font-display text-lg font-bold tracking-tight">No channels yet</h2>
              <p className="mt-2 text-sm text-[#9ca3af]">Create a channel to reuse a consistent style, tone and characters across your videos.</p>
              <Button
                data-testid="dashboard-create-first-channel"
                onClick={() => navigate("/channels", { state: { editChannelId: "__new__" } })}
                className="mt-5 rounded-md bg-[#00e5ff] text-black hover:bg-[#33ebff] font-semibold"
              >
                <Plus className="mr-2 h-4 w-4" /> Create your first channel
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-2" data-testid="dashboard-grid">
              {channels.map((c) => (
                <ChannelCard
                  key={c.channel_id}
                  channel={c}
                  onNewStory={onNewStory}
                  onManage={onManage}
                  onOpenStory={onOpenStory}
                  onHide={onHide}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
